/**
 * useTheme.js - Tema y tokens de UI desde el contexto global.
 *
 * Los componentes reutilizables deben usar estos hooks en lugar de
 * hardcodear hex o ramificar solo con isDarkMode.
 */

import { useContext, useMemo } from 'react';
import { getTheme, getUIColors } from '../components/layout/theme';
import { ThemeContext } from '../contexts/ThemeContext';

/**
 * Tema completo activo.
 * @param {boolean|string} [override] - Opcional: fuerza un modo (tests / override local)
 */
export const useTheme = (override) => {
  const ctx = useContext(ThemeContext);

  return useMemo(() => {
    if (typeof override === 'string') {
      return getTheme(override);
    }
    if (ctx?.theme && override === undefined) {
      return ctx.theme;
    }
    if (ctx?.colorMode && (override === true || override === false)) {
      // Preferir colorMode del contexto para soportar Regular
      return getTheme(ctx.colorMode);
    }
    if (typeof override === 'boolean') {
      return getTheme(override);
    }
    if (ctx?.theme) {
      return ctx.theme;
    }
    return getTheme();
  }, [override, ctx?.theme, ctx?.colorMode]);
};

/**
 * Tokens de chrome unificados (tablas, cards, forms).
 */
export const useUIColors = () => {
  const ctx = useContext(ThemeContext);
  const theme = useTheme();

  return useMemo(() => {
    if (ctx?.ui) {
      return {
        ...ctx.ui,
        theme,
        colorMode: ctx.colorMode,
        isDarkMode: ctx.isDarkMode,
      };
    }
    return {
      ...getUIColors(theme),
      theme,
      colorMode: 'light',
      isDarkMode: false,
    };
  }, [ctx?.ui, ctx?.colorMode, ctx?.isDarkMode, theme]);
};

export default useTheme;
